<template>
  <div class="copilot-chat" :class="{ 'cc-has-doc-panel': hasDocumentOpen }">
    <!-- ── Main chat area ─────────────────────────── -->
    <div class="cc-main">
      <!-- Topbar -->
      <div class="cc-topbar">
        <div class="cc-topbar-left">
          <button
            class="cc-hamburger"
            @click="$emit('toggle-sidebar')"
            title="Conversaciones"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h3 class="cc-title">{{ sessionTitle }}</h3>
          <span class="cc-live-badge">Datos en vivo</span>
        </div>
        <div class="cc-topbar-right">
          <button
            class="cc-topbar-close"
            @click="closeCopilot"
            title="Cerrar Copilot"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Messages area -->
      <div class="cc-messages" ref="messagesArea">
        <!-- Welcome screen -->
        <div v-if="currentMessages.length === 0" class="cc-welcome">
          <div class="cc-welcome-icon">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#3ecfb4"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z"
              />
            </svg>
          </div>
          <h2 class="cc-welcome-title">Hola {{ userName }}.</h2>
          <p class="cc-welcome-sub">
            Soy tu agente de negocios. Analizo tus finanzas, creo documentos,
            redacto contratos, diseño estrategias de crecimiento y ejecuto
            acciones en tu negocio. Cada consulta consume 1 crédito de IA.
          </p>
          <div class="cc-welcome-chips">
            <button
              class="copilot-action-chip"
              @click="
                quickSend(
                  '¿Cuál es mi resumen de ventas este mes vs el anterior?'
                )
              "
            >
              📊 Resumen de ventas
            </button>
            <button
              class="copilot-action-chip"
              @click="
                quickSend(
                  '¿Qué cotizaciones aprobadas tengo pendientes de facturar?'
                )
              "
            >
              📋 Cotizaciones sin facturar
            </button>
            <button
              class="copilot-action-chip"
              @click="
                quickSend(
                  'Dame un análisis estratégico de mi negocio: ¿qué oportunidades de crecimiento tengo y qué debo priorizar?'
                )
              "
            >
              🚀 Estrategia de crecimiento
            </button>
            <button
              class="copilot-action-chip"
              @click="
                quickSend(
                  '¿Cuáles son mis alertas urgentes y qué debo priorizar hoy?'
                )
              "
            >
              🚨 Alertas urgentes
            </button>
          </div>
        </div>

        <!-- Messages -->
        <template v-for="msg in currentMessages">
          <!-- Credit confirmation card -->
          <div
            v-if="
              msg.role === 'system' && msg.content === '__CREDIT_CONFIRMATION__'
            "
            :key="msg.id"
            class="cc-credit-confirm"
          >
            <div class="cc-confirm-header">
              <span class="cc-confirm-icon">⚡</span>
              <span class="cc-confirm-title"
                >Esta solicitud consume
                {{ msg.creditData.estimated_credits }} créditos</span
              >
            </div>
            <div class="cc-confirm-reason">{{ msg.creditData.reason }}</div>
            <div
              class="cc-confirm-balance"
              v-if="
                msg.creditData.current_credits &&
                  !msg.creditData.current_credits.unlimited
              "
            >
              Créditos disponibles:
              <strong>{{ msg.creditData.current_credits.remaining }}</strong> de
              {{ msg.creditData.current_credits.limit }}
            </div>
            <div class="cc-confirm-actions">
              <button
                class="cc-confirm-approve"
                @click="approveCredits(msg)"
                :disabled="isSending"
              >
                ✅ Aprobar ({{ msg.creditData.estimated_credits }} créditos)
              </button>
              <button class="cc-confirm-cancel" @click="cancelCredits(msg)">
                Cancelar
              </button>
            </div>
          </div>

          <!-- Limit exceeded banner -->
          <div
            v-else-if="
              msg.role === 'system' && msg.content === '__LIMIT_EXCEEDED__'
            "
            :key="msg.id"
            class="cc-limit-banner"
          >
            <div class="cc-limit-icon">
              <i class="el-icon-warning-outline"></i>
            </div>
            <div class="cc-limit-body">
              <div class="cc-limit-title">Límite de uso alcanzado</div>
              <div class="cc-limit-text">
                Has alcanzado el máximo de consultas de IA de tu plan este mes.
              </div>
              <button class="cc-limit-upgrade" @click="goToPlans">
                Mejorar plan
              </button>
            </div>
          </div>

          <!-- ── Action proposal card (aprobación de acciones de escritura) ── -->
          <div
            v-else-if="
              msg.role === 'system' && msg.content === '__ACTION_PROPOSAL__'
            "
            :key="msg.id"
            class="cc-action-proposal"
            :class="{
              'cc-ap-done': msg.proposalStatus === 'done',
              'cc-ap-rejected': msg.proposalStatus === 'rejected',
              'cc-ap-error': msg.proposalStatus === 'error',
              'cc-ap-historical': msg.proposalStatus === 'historical'
            }"
          >
            <!-- Header -->
            <div class="cc-ap-header">
              <span class="cc-ap-icon">{{
                (msg.proposalData.preview || {}).icon || "⚡"
              }}</span>
              <div class="cc-ap-titles">
                <div class="cc-ap-title">
                  {{
                    (msg.proposalData.preview || {}).title || "Acción propuesta"
                  }}
                </div>
                <div class="cc-ap-desc">
                  {{
                    (msg.proposalData.preview || {}).description ||
                      "Revisa los detalles antes de aprobar"
                  }}
                </div>
              </div>
              <span class="cc-ap-badge" v-if="msg.proposalStatus === 'pending'"
                >Requiere aprobación</span
              >
              <span
                class="cc-ap-badge cc-ap-badge-done"
                v-else-if="msg.proposalStatus === 'done'"
                >✅ Ejecutado</span
              >
              <span
                class="cc-ap-badge cc-ap-badge-rejected"
                v-else-if="msg.proposalStatus === 'rejected'"
                >Cancelado</span
              >
              <span
                class="cc-ap-badge cc-ap-badge-error"
                v-else-if="msg.proposalStatus === 'error'"
                >⚠️ Error</span
              >
              <span
                class="cc-ap-badge cc-ap-badge-historical"
                v-else-if="msg.proposalStatus === 'historical'"
                >Propuesta anterior</span
              >
            </div>

            <!-- Detail items -->
            <ul
              class="cc-ap-items"
              v-if="
                (msg.proposalData.preview || {}).items &&
                  msg.proposalData.preview.items.length
              "
            >
              <li
                v-for="(item, idx) in msg.proposalData.preview.items"
                :key="idx"
              >
                <span
                  v-if="item && item !== '─────────────────'"
                  :class="{ 'cc-ap-total-line': item.startsWith('Total:') }"
                  >{{ item }}</span
                >
                <hr v-else-if="item === '─────────────────'" class="cc-ap-hr" />
              </li>
            </ul>

            <!-- Result message (after execution) -->
            <div
              class="cc-ap-result"
              v-if="
                msg.proposalStatus === 'done' ||
                  msg.proposalStatus === 'error' ||
                  msg.proposalStatus === 'rejected'
              "
            >
              <template v-if="msg.proposalStatus === 'done'">
                {{ (msg.proposalResult || {}).message || "Acción ejecutada." }}
              </template>
              <template v-else-if="msg.proposalStatus === 'error'">
                {{ (msg.proposalResult || {}).error || "Ocurrió un error." }}
              </template>
              <template v-else-if="msg.proposalStatus === 'rejected'">
                Acción cancelada. Puedes pedirme que la modifique y volver a
                intentarlo.
              </template>
            </div>

            <!-- Botón "Ver documento" tras ejecución exitosa -->
            <div
              v-if="
                msg.proposalStatus === 'done' &&
                  getDocumentRoute(msg.proposalResult)
              "
              class="cc-ap-view-doc-wrap"
            >
              <button
                class="cc-ap-btn-view-doc"
                @click="openCreatedDocument(msg.proposalResult)"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                {{ getDocumentRoute(msg.proposalResult).label }}
              </button>
            </div>

            <!-- Chips de siguiente paso — proactivos tras ejecutar la acción -->
            <div
              v-if="
                msg.proposalStatus === 'done' && getFollowUpChips(msg).length
              "
              class="cc-ap-followup"
            >
              <button
                v-for="(chip, idx) in getFollowUpChips(msg)"
                :key="idx"
                class="cc-ap-followup-chip"
                @click="quickSend(chip.prompt)"
              >
                {{ chip.icon }} {{ chip.label }}
              </button>
            </div>

            <!-- Actions buttons -->
            <div class="cc-ap-actions" v-if="msg.proposalStatus === 'pending'">
              <button
                class="cc-ap-btn-approve"
                @click="approveAction(msg)"
                :disabled="msg.proposalStatus !== 'pending'"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Aprobar
              </button>
              <button
                class="cc-ap-btn-reject"
                @click="rejectAction(msg)"
                :disabled="msg.proposalStatus !== 'pending'"
              >
                Cancelar
              </button>
            </div>
            <div
              class="cc-ap-loading"
              v-else-if="msg.proposalStatus === 'loading'"
            >
              <span class="cc-ap-spinner"></span> Ejecutando…
            </div>
          </div>

          <!-- Normal message -->
          <div
            v-else
            :key="msg.id"
            class="cc-msg"
            :class="{ 'cc-msg-user': msg.role === 'user' }"
          >
            <div
              class="cc-msg-avatar"
              :class="{ 'cc-msg-avatar-ai': msg.role === 'assistant' }"
            >
              {{ msg.role === "user" ? userInitials : "IA" }}
            </div>
            <div class="cc-msg-body">
              <div class="cc-msg-meta">
                {{ msg.role === "user" ? "Tú" : "Mydessk IA" }}
                <span class="cc-msg-time">{{
                  formatTime(msg.created_at)
                }}</span>
              </div>
              <!-- Waiting dots — shown inside the bubble before any content arrives -->
              <div
                v-if="msg.streaming && !msg.content && !msg.toolStatus"
                class="cc-typing-dots"
              >
                <span></span><span></span><span></span>
              </div>
              <!-- Tool execution / thinking indicator -->
              <div
                v-else-if="msg.toolStatus === 'executing'"
                class="cc-tool-executing"
              >
                <span class="cc-tool-spinner"></span> Ejecutando acción...
              </div>
              <div
                v-else-if="
                  msg.toolStatus === 'done' || msg.toolStatus === 'thinking'
                "
                class="cc-tool-executing"
              >
                <span class="cc-tool-spinner"></span> Procesando resultados...
              </div>
              <!-- Message text (preamble before artifact, or normal content) -->
              <div
                class="cc-msg-text"
                :class="{
                  'cc-streaming': msg.streaming || msg.id === streamingMessageId
                }"
                v-if="getCleanContent(msg)"
                v-html="
                  msg.streaming
                    ? formatStreamingText(msg.content)
                    : msg.id === streamingMessageId
                    ? formatStreamingText(streamingText)
                    : formatMessage(msg.content)
                "
              ></div>
              <!-- Document artifact card — shows immediately when <artifact> tag starts streaming -->
              <div
                v-if="parseStreamingArtifact(msg.content)"
                class="cc-doc-card"
                :class="{
                  'cc-doc-card-live':
                    msg.streaming && !parseArtifact(msg.content)
                }"
                @click="openLiveDocument(msg)"
              >
                <div class="cc-doc-card-icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                    />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </div>
                <div class="cc-doc-card-info">
                  <div class="cc-doc-card-badge">
                    <span
                      v-if="msg.streaming && !parseArtifact(msg.content)"
                      class="cc-doc-badge-live"
                    >
                      <span class="cc-doc-badge-dot"></span> Generando...
                    </span>
                    <span v-else>{{
                      getDocTypeName(parseStreamingArtifact(msg.content).type)
                    }}</span>
                  </div>
                  <div class="cc-doc-card-title">
                    {{ parseStreamingArtifact(msg.content).title }}
                  </div>
                  <div class="cc-doc-card-hint">
                    {{
                      msg.streaming && !parseArtifact(msg.content)
                        ? "Abre para ver en tiempo real"
                        : "Haz clic para previsualizar"
                    }}
                  </div>
                </div>
                <div class="cc-doc-card-arrow">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>
              <!-- Action cards for executed actions (only meaningful ones) -->
              <div
                v-if="getVisibleActions(msg.actions).length > 0"
                class="cc-action-cards"
              >
                <div
                  v-for="(action, idx) in getVisibleActions(msg.actions)"
                  :key="idx"
                  class="cc-action-card"
                  :class="getActionCardClass(action)"
                >
                  <div class="cc-action-icon">
                    {{ getActionIcon(action) }}
                  </div>
                  <div class="cc-action-info">
                    <div class="cc-action-label">
                      {{ getActionLabel(action) }}
                    </div>
                    <div
                      v-if="action.result && action.result.message"
                      class="cc-action-detail"
                    >
                      {{ action.result.message }}
                    </div>
                    <div
                      v-if="action.result && action.result.total"
                      class="cc-action-total"
                    >
                      Total: ${{ action.result.total }}
                    </div>
                    <div
                      v-if="action.result && action.result.error"
                      class="cc-action-error"
                    >
                      {{ action.result.error }}
                    </div>
                  </div>
                  <div class="cc-action-buttons">
                    <button
                      v-if="getActionRoute(action)"
                      class="cc-action-go"
                      @click="navigateToAction(action)"
                      title="Ver detalle"
                    >
                      Ver →
                    </button>
                    <button
                      v-if="
                        action.result &&
                          action.result.success &&
                          action.tool === 'create_quote_draft'
                      "
                      class="cc-action-approve"
                      @click="
                        quickSend('Convierte esa cotización a factura borrador')
                      "
                      title="Convertir a factura"
                    >
                      🧾 Facturar
                    </button>
                    <button
                      v-if="
                        action.result &&
                          action.result.success &&
                          (action.tool === 'create_quote_draft' ||
                            action.tool === 'create_invoice_draft')
                      "
                      class="cc-action-approve"
                      @click="
                        quickSend(
                          'Envía por correo al cliente el documento que acabas de crear'
                        )
                      "
                      title="Enviar por email"
                    >
                      📧 Enviar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Input area -->
      <div class="cc-input-zone">
        <!-- Credits bar — solo visible con pocos créditos y antes del primer mensaje -->
        <div v-if="showCreditsWarning" class="cc-credits-bar">
          <div class="cc-credits-info">
            <span class="cc-credits-icon">⚡</span>
            <span class="cc-credits-text">
              <strong>{{ credits.remaining }}</strong> créditos restantes
            </span>
            <span class="cc-credits-detail">
              ({{ credits.used }}/{{ credits.limit }} usados este mes)
            </span>
          </div>
          <div class="cc-credits-progress">
            <div
              class="cc-credits-fill"
              :class="creditsBarClass"
              :style="{ width: credits.percentage + '%' }"
            ></div>
          </div>
        </div>

        <div class="cc-input-box" :class="{ focused: isFocused }">
          <textarea
            v-model="inputText"
            ref="inputArea"
            placeholder="Pregunta sobre tu negocio, pide documentos, estrategias o acciones..."
            @keydown.enter.exact.prevent="send"
            @focus="isFocused = true"
            @blur="isFocused = false"
            @input="autoResize"
            rows="1"
            :disabled="isSending"
          ></textarea>
          <button
            class="cc-send-btn"
            @click="send"
            :disabled="!inputText.trim() || isSending"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
        <div class="cc-hints">
          <span class="cc-hint-label">Sugerencias:</span>
          <button
            v-for="(chip, idx) in suggestionChips"
            :key="idx"
            class="cc-hint-chip"
            @click="quickSend(chip.prompt)"
          >
            {{ chip.icon }} {{ chip.label }}
          </button>
        </div>
      </div>
    </div>
    <!-- end cc-main -->

    <!-- ── Document preview panel ────────────────── -->
    <transition name="cc-doc-slide">
      <div
        v-if="hasDocumentOpen"
        class="cc-doc-panel"
        :class="{ 'cc-doc-panel-streaming': !!liveDocMessageId }"
      >
        <div class="cc-doc-panel-header">
          <div class="cc-doc-panel-meta">
            <span class="cc-doc-panel-badge">
              <span v-if="liveDocMessageId" class="cc-doc-badge-live">
                <span class="cc-doc-badge-dot"></span> Generando
              </span>
              <span v-else>{{ getDocTypeName(displayedDocument.type) }}</span>
            </span>
            <h3 class="cc-doc-panel-title">{{ displayedDocument.title }}</h3>
          </div>
          <div class="cc-doc-panel-actions">
            <button
              class="cc-doc-action-btn"
              @click="copyDocument"
              :class="{ 'cc-doc-copied': docCopied }"
              :disabled="!!liveDocMessageId"
              title="Copiar contenido"
            >
              <svg
                v-if="!docCopied"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              <svg
                v-else
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {{ docCopied ? "Copiado" : "Copiar" }}
            </button>
            <div class="cc-download-group">
              <button
                class="cc-doc-icon-btn cc-doc-download-btn"
                @click="downloadDocument"
                :disabled="!!liveDocMessageId"
                title="Descargar PDF"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span class="cc-dl-label">PDF</span>
              </button>
              <button
                class="cc-doc-icon-btn cc-doc-download-btn"
                @click="downloadDocx"
                :disabled="!!liveDocMessageId"
                title="Descargar DOCX (Word)"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span class="cc-dl-label">DOCX</span>
              </button>
            </div>
            <button
              class="cc-doc-close-btn"
              @click="closeDocPanel"
              title="Cerrar"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
        <div class="cc-doc-panel-body" ref="docPanelBody">
          <div
            class="cc-doc-content"
            v-html="formatDocument(displayedDocument.content)"
          ></div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Api from "@/config/Api";
import { downloadFile } from "@/assets/utils/files";

export default {
  name: "CopilotChat",
  data() {
    return {
      inputText: "",
      isFocused: false,
      streamingMessageId: null,
      streamingText: "",
      streamingTimer: null,
      lastAnimatedId: null,
      activeDocument: null, // static document (finalized)
      liveDocMessageId: null, // tracks the streaming message with an artifact
      docCopied: false
    };
  },
  computed: {
    currentMessages() {
      return this.$store.getters["copilot/currentMessages"];
    },
    isLoading() {
      return this.$store.getters["copilot/isLoading"];
    },
    isSending() {
      return this.$store.getters["copilot/isSending"];
    },
    currentSessionId() {
      return this.$store.getters["copilot/currentSessionId"];
    },
    sessionTitle() {
      if (!this.currentSessionId) return "Mydessk IA";
      const s = this.$store.getters["copilot/sessions"].find(
        s => s.id === this.currentSessionId
      );
      return s ? s.title : "Mydessk IA";
    },
    credits() {
      return this.$store.getters["copilot/credits"];
    },
    // Chips dinámicos según estado real del negocio (vencidas, aprobadas sin
    // facturar, dormidos). Fallback a los 4 fijos mientras carga o si falla.
    suggestionChips() {
      const dynamic = this.$store.getters["copilot/suggestions"];
      if (dynamic && dynamic.length) return dynamic;
      return [
        {
          icon: "📈",
          label: "Meta mensual",
          prompt:
            "¿Cuánto llevo facturado este mes y cuánto me falta para la meta?"
        },
        {
          icon: "🎯",
          label: "Conversión",
          prompt: "¿Cómo está mi tasa de conversión y qué puedo mejorar?"
        },
        {
          icon: "💰",
          label: "Cobros pendientes",
          prompt: "¿Qué facturas tengo vencidas y a quiénes les debo cobrar?"
        },
        {
          icon: "📋",
          label: "Crear cotización",
          prompt:
            "Crea una cotización borrador para mi último cliente con el producto más vendido"
        }
      ];
    },
    showCreditsWarning() {
      if (!this.credits || this.credits.unlimited) return false;
      if (this.credits.percentage < 70) return false;
      // Ocultar si el usuario ya envió un mensaje en esta sesión
      const userMessages = this.currentMessages.filter(m => m.role === "user");
      if (userMessages.length > 0) return false;
      return true;
    },
    creditsBarClass() {
      if (!this.credits) return "";
      if (this.credits.percentage >= 90) return "cc-credits-critical";
      if (this.credits.percentage >= 70) return "cc-credits-warning";
      return "cc-credits-ok";
    },
    userName() {
      const user = this.$store.getters.getUser;
      return user ? user.name : "";
    },
    userInitials() {
      const user = this.$store.getters.getUser;
      if (!user) return "";
      const first = user.name ? user.name[0] : "";
      const last = user.last_name ? user.last_name[0] : "";
      return (first + last).toUpperCase();
    },
    // Live document: either the streaming artifact or a finalized one
    displayedDocument() {
      if (this.liveDocMessageId) {
        const msg = this.currentMessages.find(
          m => m.id === this.liveDocMessageId
        );
        if (msg) {
          const artifact = this.parseStreamingArtifact(msg.content);
          if (artifact) return artifact;
        }
        // Message gone or no artifact — fall through to static
      }
      return this.activeDocument;
    },
    hasDocumentOpen() {
      return !!this.displayedDocument;
    }
  },
  watch: {
    currentMessages: {
      handler(messages) {
        this.$nextTick(() => this.scrollToBottom());

        // ── Live document panel tracking ──────────────────────────
        // Find any streaming message that has started an artifact (early detection)
        const streamingWithArtifact = messages.find(
          m => m.streaming && this.isGeneratingDocument(m.content)
        );
        if (
          streamingWithArtifact &&
          this.liveDocMessageId !== streamingWithArtifact.id
        ) {
          // Artifact just started — open panel in live mode
          this.liveDocMessageId = streamingWithArtifact.id;
          this.activeDocument = null;
          this.docCopied = false;
        }

        // When live streaming message finishes, finalize to static document
        if (this.liveDocMessageId) {
          const tracked = messages.find(m => m.id === this.liveDocMessageId);
          if (tracked && !tracked.streaming) {
            const finished = this.parseArtifact(tracked.content);
            if (finished) this.activeDocument = finished;
            this.liveDocMessageId = null;
          }
        }

        // Scroll the doc panel as content streams in
        if (this.liveDocMessageId && this.$refs.docPanelBody) {
          this.$nextTick(() => {
            const body = this.$refs.docPanelBody;
            if (body) body.scrollTop = body.scrollHeight;
          });
        }

        // ── Message animation (history reveal) ───────────────────
        const isLoadingSession = this.$store.getters[
          "copilot/isLoadingSession"
        ];
        if (isLoadingSession) return;
        const last = messages[messages.length - 1];
        if (last && last.role === "assistant") {
          if (last.streaming) {
            this.lastAnimatedId = last.id;
          } else if (
            last.id !== this.lastAnimatedId &&
            last.content &&
            last.content !== "__LIMIT_EXCEEDED__"
          ) {
            this.lastAnimatedId = last.id;
            this.startStreaming(last);
          }
        }
      },
      deep: true
    },
    isLoading() {
      this.$nextTick(() => this.scrollToBottom());
    },
    currentSessionId() {
      // Stop any ongoing streaming animation
      if (this.streamingTimer) {
        clearInterval(this.streamingTimer);
        this.streamingTimer = null;
        this.streamingMessageId = null;
      }
      // Reset document panel when switching sessions
      this.liveDocMessageId = null;
      this.activeDocument = null;
      // Mark last message of new session as seen so it doesn't re-stream
      this.$nextTick(() => {
        const msgs = this.currentMessages;
        if (msgs.length) {
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant") {
            this.lastAnimatedId = last.id;
          }
        }
        this.$nextTick(() => this.scrollToBottom());
      });
    }
  },
  methods: {
    send() {
      const text = this.inputText.trim();
      if (!text || this.isSending) return;

      // Optimistic: add user message immediately
      this.$store.commit("copilot/ADD_MESSAGE", {
        id: Date.now(),
        role: "user",
        content: text,
        created_at: new Date().toISOString()
      });

      this.inputText = "";
      this.resetTextarea();

      this.$store.dispatch("copilot/sendMessage", text);

      this.$nextTick(() => {
        if (this.$refs.inputArea) {
          this.$refs.inputArea.focus();
        }
      });
    },
    quickSend(text) {
      this.inputText = text;
      this.send();
    },
    autoResize() {
      const el = this.$refs.inputArea;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    },
    resetTextarea() {
      this.$nextTick(() => {
        if (this.$refs.inputArea) {
          this.$refs.inputArea.style.height = "auto";
        }
      });
    },
    scrollToBottom() {
      const area = this.$refs.messagesArea;
      if (area) {
        area.scrollTop = area.scrollHeight;
      }
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleTimeString("es-EC", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    closeCopilot() {
      this.$store.dispatch("copilot/close");
    },
    goToPlans() {
      this.$store.dispatch("copilot/close");
      this.$router.push({ name: "plans" });
    },
    startStreaming(msg) {
      if (this.streamingTimer) clearInterval(this.streamingTimer);
      this.streamingMessageId = msg.id;
      this.streamingText = "";
      const fullText = msg.content;
      const len = fullText.length;
      // Adaptive: cap total duration at 1500ms
      const duration = Math.min(len * 5, 1500);
      const tickMs = 16;
      const charsPerTick = Math.max(1, Math.ceil(len / (duration / tickMs)));
      let idx = 0;
      this.streamingTimer = setInterval(() => {
        idx += charsPerTick;
        this.streamingText = fullText.substring(0, idx);
        this.$nextTick(() => this.scrollToBottom());
        if (idx >= len) {
          this.streamingText = fullText;
          this.streamingMessageId = null;
          clearInterval(this.streamingTimer);
          this.streamingTimer = null;
        }
      }, tickMs);
    },
    // Formateador liviano usado SOLO mientras el texto está animándose (streaming real
    // o la máquina de escribir de historial). formatMessage() completo hace ~15 pasadas
    // de regex sobre el string entero — llamarlo en cada tick (hasta 60/seg) sobre un
    // documento largo satura el hilo principal y el texto se ve "a saltos". Aquí solo
    // ocultamos artifacts/contexto interno y convertimos saltos de línea — el render
    // rico (tablas, negritas, headers) se aplica una sola vez cuando el texto se asienta.
    formatStreamingText(text) {
      if (!text) return "";
      let cleaned = text
        .replace(/<artifact[\s\S]*$/, "")
        .replace(/^\[Contexto interno:[^\]]*\]\s*$/gm, "")
        .trim();
      return cleaned.replace(/\n/g, "<br>");
    },
    formatMessage(text) {
      if (!text) return "";
      // Strip complete artifact blocks (they render as document cards, not inline)
      let cleaned = text
        .replace(/<artifact[^>]*>[\s\S]*?<\/artifact>/g, "")
        .trim();
      // Strip partial artifact tag during streaming
      cleaned = cleaned.replace(/<artifact[\s\S]*$/, "").trim();
      // Ocultar líneas de contexto interno (memoria del copilot, no para el usuario)
      cleaned = cleaned
        .replace(/^\[Contexto interno:[^\]]*\]\s*$/gm, "")
        .trim();

      // Tablas markdown: detectar bloques contiguos de filas con `|` y agruparlas en <table>
      cleaned = cleaned.replace(/(?:^\|[^\n]+\|\s*\n)+/gm, function(block) {
        const rows = block
          .split("\n")
          .map(r => r.trim())
          .filter(r => r.startsWith("|") && r.endsWith("|"));
        if (rows.length === 0) return block;
        const parsed = rows.map(r =>
          r
            .slice(1, -1)
            .split("|")
            .map(c => c.trim())
        );
        // Detectar fila separadora (---) y omitirla
        const isSeparator = arr => arr.every(c => /^:?-{2,}:?$/.test(c));
        let headerRow = null;
        const bodyRows = [];
        parsed.forEach((row, idx) => {
          if (isSeparator(row)) return;
          if (idx === 0 && parsed[1] && isSeparator(parsed[1])) {
            headerRow = row;
          } else {
            bodyRows.push(row);
          }
        });
        let inner = '<table class="cc-md-table"><tbody>';
        if (headerRow) {
          inner +=
            "<tr>" + headerRow.map(c => `<th>${c}</th>`).join("") + "</tr>";
        }
        bodyRows.forEach(row => {
          inner += "<tr>" + row.map(c => `<td>${c}</td>`).join("") + "</tr>";
        });
        inner += "</tbody></table>";
        return `<div class="cc-md-table-wrap">${inner}</div>\n`;
      });

      // Headers: ## Title or ### Title → styled headers
      let html = cleaned.replace(
        /^###\s+(.+)$/gm,
        '<div class="cc-h3">$1</div>'
      );
      html = html.replace(/^##\s+(.+)$/gm, '<div class="cc-h2">$1</div>');
      html = html.replace(/^#\s+(.+)$/gm, '<div class="cc-h1">$1</div>');
      // Horizontal rules: --- or ━━
      html = html.replace(/^-{3,}$/gm, '<hr class="cc-hr">');
      html = html.replace(/^━{2,}.*$/gm, '<hr class="cc-hr">');
      // Bold: **text** → <strong>text</strong>
      html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      // Code/nav: `text` → <span class="nav-tag">text</span>
      html = html.replace(/`(.*?)`/g, '<span class="nav-tag">$1</span>');
      // Numbered lists: lines starting with 1. 2. etc.
      html = html.replace(
        /^[\s]*(\d+)\.\s+(.+)$/gm,
        '<span class="cc-list-item cc-list-numbered"><span class="cc-list-num">$1.</span> $2</span>'
      );
      // Bullet lists: lines starting with - or •
      html = html.replace(
        /^[\s]*[-•]\s+(.+)$/gm,
        '<span class="cc-list-item">• $1</span>'
      );
      // Colapsar 3+ saltos de línea consecutivos en máximo 2
      html = html.replace(/\n{3,}/g, "\n\n");
      // Double newline → <br><br>
      html = html.replace(/\n\n/g, "<br><br>");
      // Single newline → <br> (excepto inmediatamente antes/después de la tabla)
      html = html.replace(/(<\/div>)\n+(?=<br>|$|<)/g, "$1");
      html = html.replace(/\n+(<div class="cc-md-table-wrap")/g, "$1");
      html = html.replace(/(<\/table>)\n/g, "$1");
      html = html.replace(/\n(<table)/g, "$1");
      html = html.replace(/\n/g, "<br>");
      return html;
    },
    getVisibleActions(actions) {
      if (!actions || !actions.length) return [];
      // Solo mostrar acciones significativas (creaciones, envíos, conversiones)
      // Ocultar búsquedas (search_clients, search_products, get_client_history)
      const hiddenTools = [
        "search_clients",
        "search_products",
        "get_client_history",
        "get_quote_details",
        "get_invoice_details"
      ];
      return actions.filter(a => !hiddenTools.includes(a.tool));
    },
    getActionIcon(action) {
      const icons = {
        create_quote_draft: "📋",
        create_invoice_draft: "🧾",
        convert_quote_to_invoice: "🔄",
        send_email_to_client: "📧",
        search_clients: "🔍",
        search_products: "📦",
        get_client_history: "📊"
      };
      return icons[action.tool] || "⚡";
    },
    getActionLabel(action) {
      const labels = {
        create_quote_draft: "Cotización creada",
        create_invoice_draft: "Factura creada",
        convert_quote_to_invoice: "Cotización convertida",
        send_email_to_client: "Correo enviado",
        search_clients: "Clientes encontrados",
        search_products: "Productos encontrados",
        get_client_history: "Historial consultado"
      };
      return labels[action.tool] || action.tool;
    },
    getActionCardClass(action) {
      if (action.result && action.result.error) return "cc-action-error-card";
      if (action.result && action.result.success)
        return "cc-action-success-card";
      return "";
    },
    getActionRoute(action) {
      if (!action.result || !action.result.success) return null;
      if (action.tool === "create_quote_draft" && action.result.quote_id) {
        return {
          name: "sales.quotes.view",
          params: {
            id: action.result.quote_id,
            idVersion: action.result.version_id || 1
          }
        };
      }
      if (action.tool === "create_invoice_draft" && action.result.invoice_id) {
        return {
          name: "sales.invoices.view",
          params: { id: action.result.invoice_id }
        };
      }
      if (
        action.tool === "convert_quote_to_invoice" &&
        action.result.invoice_id
      ) {
        return {
          name: "sales.invoices.view",
          params: { id: action.result.invoice_id }
        };
      }
      return null;
    },
    approveCredits(msg) {
      if (!msg.creditData) return;
      // Reemplazar la card de confirmación con un mensaje de "aprobado"
      msg.content = "✅ Aprobado — procesando solicitud...";
      msg.role = "assistant";
      // Enviar con confirmación
      this.$store.dispatch("copilot/confirmCredits", {
        text: msg.creditData.original_message,
        credits: msg.creditData.estimated_credits
      });
    },
    cancelCredits(msg) {
      // Reemplazar la card con mensaje de cancelación
      msg.content = "❌ Solicitud cancelada. No se consumieron créditos.";
      msg.role = "assistant";
      msg.creditData = null;
    },

    // ── Action proposal ────────────────────────────
    async approveAction(msg) {
      if (!msg.proposalData || msg.proposalStatus !== "pending") return;
      await this.$store.dispatch("copilot/executeAction", {
        proposalId: msg.id,
        action: msg.proposalData.action,
        params: msg.proposalData.params
      });
    },
    rejectAction(msg) {
      if (msg.proposalStatus !== "pending") return;
      this.$store.dispatch("copilot/rejectAction", { proposalId: msg.id });
    },

    /**
     * Sugiere el siguiente paso lógico tras ejecutar una acción — evita que el
     * usuario tenga que escribir de nuevo lo obvio (facturar, enviar por correo).
     * Solo se muestra cuando la acción se ejecutó con éxito.
     */
    getFollowUpChips(msg) {
      if (!msg.proposalData || msg.proposalStatus !== "done") return [];
      const tool = msg.proposalData.action;
      const result = msg.proposalResult || {};
      if (!result || !result.success) return [];

      if (tool === "create_quote_draft") {
        return [
          {
            icon: "🧾",
            label: "Facturar",
            prompt: "Convierte esa cotización a factura borrador"
          },
          {
            icon: "📧",
            label: "Enviar por correo",
            prompt: "Envía esa cotización por correo al cliente"
          }
        ];
      }
      if (
        tool === "create_invoice_draft" ||
        tool === "convert_quote_to_invoice"
      ) {
        return [
          {
            icon: "📧",
            label: "Enviar por correo",
            prompt: "Envía esa factura por correo al cliente"
          }
        ];
      }
      if (tool === "change_quote_status" && result.new_status === "success") {
        return [
          {
            icon: "🧾",
            label: "Facturar",
            prompt: "Convierte esa cotización a factura borrador"
          }
        ];
      }
      return [];
    },

    /**
     * Determina si el resultado de una acción tiene un documento abrible
     * y devuelve la ruta + label del botón.
     */
    getDocumentRoute(result) {
      if (!result || typeof result !== "object") return null;
      // Factura (created_invoice o convert_quote_to_invoice)
      if (result.invoice_id) {
        return {
          name: "sales.invoices.view",
          params: { id: String(result.invoice_id) },
          label: "Ver factura"
        };
      }
      // Cotización (create_quote_draft) — necesita id + idVersion
      if (result.quote_id && result.version_id) {
        return {
          name: "sales.quotes.view",
          params: {
            id: String(result.quote_id),
            idVersion: String(result.version_id)
          },
          label: "Ver cotización"
        };
      }
      return null;
    },

    /**
     * Cierra el copilot y navega al documento creado.
     */
    openCreatedDocument(result) {
      const route = this.getDocumentRoute(result);
      if (!route) return;
      this.$store.dispatch("copilot/close");
      // Esperar el cierre del modal antes de navegar
      this.$nextTick(() => {
        this.$router.push(route).catch(() => {});
      });
    },

    navigateToAction(action) {
      const route = this.getActionRoute(action);
      if (route) {
        this.$store.dispatch("copilot/close");
        this.$router.push(route).catch(() => {});
      }
    },

    // ── Document artifact ──────────────────────────
    parseArtifact(content) {
      if (!content) return null;
      const match = content.match(
        /<artifact\s+title="([^"]+)"\s+type="([^"]+)">([\s\S]*?)<\/artifact>/
      );
      if (!match) return null;
      return { title: match[1], type: match[2], content: match[3].trim() };
    },
    // Parse complete OR partial artifact (for live streaming display).
    // Triggers as soon as <artifact is detected — doesn't wait for full opening tag.
    parseStreamingArtifact(content) {
      if (!content) return null;
      // 1. Complete artifact — ideal case
      const complete = this.parseArtifact(content);
      if (complete) return complete;
      // 2. Full opening tag present, body streaming (no closing tag yet)
      const partial = content.match(
        /<artifact\s+title="([^"]+)"\s+type="([^"]+)">([\s\S]*)$/
      );
      if (partial)
        return {
          title: partial[1],
          type: partial[2],
          content: partial[3] || "",
          streaming: true
        };
      // 3. Opening tag still arriving — extract whatever title/type we have so far
      if (/<artifact[\s ]/.test(content)) {
        const titleMatch = content.match(/title="([^"]*)/);
        const typeMatch = content.match(/type="([^"]*)/);
        return {
          title: titleMatch ? titleMatch[1] || "Documento" : "Documento",
          type: typeMatch ? typeMatch[1] || "document" : "document",
          content: "",
          streaming: true
        };
      }
      return null;
    },
    // Returns true if the message has chat-visible text (after stripping artifact blocks)
    // Used to decide whether to show the cc-msg-text div at all
    getCleanContent(msg) {
      if (!msg) return false;
      // During history-reveal animation, use the animating text
      if (!msg.streaming && msg.id === this.streamingMessageId) {
        return this.streamingText && this.streamingText.trim().length > 0;
      }
      const src = msg.content || "";
      // Strip complete artifact blocks
      let cleaned = src
        .replace(/<artifact[^>]*>[\s\S]*?<\/artifact>/g, "")
        .trim();
      // Strip partial artifact (streaming)
      cleaned = cleaned.replace(/<artifact[\s\S]*$/, "").trim();
      return cleaned.length > 0;
    },
    isGeneratingDocument(content) {
      if (!content) return false;
      // Matches from the very first chunk of the opening tag
      return /<artifact[\s ]/.test(content) && !/<\/artifact>/.test(content);
    },
    // Open the document panel for a message (live if still streaming, static if done)
    openLiveDocument(msg) {
      this.docCopied = false;
      if (msg.streaming) {
        this.liveDocMessageId = msg.id;
        this.activeDocument = null;
      } else {
        const artifact = this.parseArtifact(msg.content);
        if (artifact) {
          this.activeDocument = artifact;
          this.liveDocMessageId = null;
        }
      }
    },
    openDocument(artifact) {
      this.activeDocument = artifact;
      this.liveDocMessageId = null;
      this.docCopied = false;
    },
    closeDocPanel() {
      this.activeDocument = null;
      this.liveDocMessageId = null;
    },
    copyDocument() {
      const doc = this.displayedDocument;
      if (!doc) return;
      navigator.clipboard.writeText(doc.content).then(() => {
        this.docCopied = true;
        setTimeout(() => {
          this.docCopied = false;
        }, 2500);
      });
    },
    async downloadDocument() {
      const doc = this.displayedDocument;
      if (!doc || !doc.content) return;
      const title = doc.title || "Documento";
      const html = this.markdownToHtml(doc.content);
      try {
        const response = await Api().post(
          "/copilot/download-document",
          { html, title },
          { responseType: "arraybuffer" }
        );
        if (response && response.data) {
          downloadFile(response.data, title);
        }
      } catch (e) {
        this.$notifications
          ? this.$notifications.error(
              "No se pudo generar el PDF. Intenta de nuevo."
            )
          : alert("No se pudo generar el PDF.");
      }
    },

    async downloadDocx() {
      const doc = this.displayedDocument;
      if (!doc || !doc.content) return;
      const title = doc.title || "Documento";
      try {
        const response = await Api().post(
          "/copilot/download-docx",
          { markdown: doc.content, title },
          { responseType: "arraybuffer" }
        );
        if (response && response.data) {
          const blob = new Blob([response.data], {
            type:
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = title + ".docx";
          a.click();
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        }
      } catch (e) {
        this.$notifications
          ? this.$notifications.error(
              "No se pudo generar el DOCX. Intenta de nuevo."
            )
          : alert("No se pudo generar el DOCX.");
      }
    },

    // Converts the markdown stored in artifacts to clean HTML for PDF printing
    markdownToHtml(text) {
      if (!text) return "";
      let h = text
        // Headers
        .replace(/^### (.+)$/gm, "<h3>$1</h3>")
        .replace(/^## (.+)$/gm, "<h2>$1</h2>")
        .replace(/^# (.+)$/gm, "<h1>$1</h1>")
        // Bold / italic
        .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        // Horizontal rules
        .replace(/^---+$/gm, "<hr>")
        .replace(/^━{2,}.*$/gm, "<hr>")
        // Numbered lists — wrap consecutive items
        .replace(/^(\d+)\. (.+)$/gm, "<li>$2</li>")
        // Bullet lists
        .replace(/^[-•*] (.+)$/gm, "<li>$1</li>")
        // Inline code
        .replace(/`([^`]+)`/g, "<code>$1</code>");

      // Wrap adjacent <li> in <ol> or <ul>
      h = h.replace(/(<li>.*?<\/li>\n?)+/gs, m => `<ul>${m}</ul>`);

      // Paragraphs: double newline → new paragraph
      h = h
        .split(/\n{2,}/)
        .map(block => {
          if (/^<(h[123]|ul|ol|hr|table)/i.test(block.trim()))
            return block.trim();
          const cleaned = block.replace(/\n/g, " ").trim();
          return cleaned ? `<p>${cleaned}</p>` : "";
        })
        .join("\n");

      return h;
    },
    getDocTypeName(type) {
      const names = {
        contract: "Contrato",
        proposal: "Propuesta comercial",
        letter: "Carta",
        agreement: "Acuerdo"
      };
      return names[type] || "Documento";
    },
    formatDocument(content) {
      if (!content) return "";
      // Full document formatter — same as formatMessage but preserves more structure
      return this.formatMessage(content);
    }
  },
  mounted() {
    // Mark existing messages as already seen so they don't re-stream
    const msgs = this.currentMessages;
    if (msgs.length) {
      const last = msgs[msgs.length - 1];
      if (last && last.role === "assistant") {
        this.lastAnimatedId = last.id;
      }
    }
    // Scroll to bottom when chat mounts (modal opens)
    this.$nextTick(() => {
      this.$nextTick(() => this.scrollToBottom());
    });
  },
  beforeDestroy() {
    if (this.streamingTimer) clearInterval(this.streamingTimer);
  }
};
</script>

<style scoped lang="scss">
.copilot-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
}

/* Topbar */
.cc-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e4eaf6;
  background: #fff;
  flex-shrink: 0;
}
.cc-topbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}
.cc-topbar-right {
  flex-shrink: 0;
  margin-left: 12px;
}
.cc-topbar-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: #909399;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: #f5f7fa;
    color: #2d2b55;
  }
}
.cc-title {
  font-size: 15px;
  font-weight: 700;
  color: #2d2b55;
  margin: 0;
}
.cc-hamburger {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #606266;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover {
    background: #f4f5f9;
    color: #2d2b55;
  }
}
.cc-live-badge {
  font-size: 10px;
  padding: 3px 10px;
  border-radius: 10px;
  background: #e1f5ee;
  color: #1d9e75;
  font-weight: 600;
}

/* Messages — scroll general del contenido */
.cc-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
}

/* Welcome */
.cc-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px;
  flex: 1;
}
.cc-welcome-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f0fdf7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.cc-welcome-title {
  font-size: 22px;
  font-weight: 700;
  color: #2d2b55;
  margin: 0 0 8px;
}
.cc-welcome-sub {
  font-size: 14px;
  color: #606266;
  margin: 0 0 20px;
  max-width: 380px;
  line-height: 1.5;
}
.cc-welcome-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

/* Message bubble */
.cc-msg {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.cc-msg-user {
  flex-direction: row-reverse;
  .cc-msg-body {
    align-items: flex-end;
  }
  .cc-msg-text {
    background: #2d2b55;
    color: #fff;
    border-radius: 16px 16px 4px 16px;
  }
}
.cc-msg-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #2d2b55;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.cc-msg-avatar-ai {
  background: #e1f5ee;
  color: #1d9e75;
}
.cc-msg-body {
  display: flex;
  flex-direction: column;
  max-width: 80%;
  min-width: 0;
}
.cc-msg-meta {
  font-size: 11px;
  color: #909399;
  margin-bottom: 4px;
  font-weight: 500;
}
.cc-msg-time {
  margin-left: 6px;
}
.cc-msg-text {
  font-size: 13.5px;
  line-height: 1.55;
  color: #303133;
  background: #f4f5f9;
  padding: 10px 14px;
  border-radius: 16px 16px 16px 4px;
  word-break: break-word;
}

/* Action cards */
.cc-action-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
.cc-action-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8f9fc;
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  transition: all 0.15s;
}
.cc-action-success-card {
  background: #f0fdf7;
  border-color: #c6f0df;
}
.cc-action-error-card {
  background: #fff5f5;
  border-color: #fde2e2;
}
.cc-action-icon {
  font-size: 20px;
  flex-shrink: 0;
}
.cc-action-info {
  flex: 1;
  min-width: 0;
}
.cc-action-label {
  font-size: 12px;
  font-weight: 700;
  color: #2d2b55;
  margin-bottom: 2px;
}
.cc-action-detail {
  font-size: 11.5px;
  color: #606266;
  line-height: 1.4;
  word-break: break-word;
}
.cc-action-total {
  font-size: 12px;
  font-weight: 700;
  color: #1d9e75;
  margin-top: 2px;
}
.cc-action-error {
  font-size: 11.5px;
  color: #f56c6c;
  line-height: 1.4;
}
.cc-action-buttons {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  flex-wrap: wrap;
}
.cc-action-go {
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  background: #fff;
  color: #2d2b55;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    background: #2d2b55;
    color: #fff;
    border-color: #2d2b55;
  }
}
.cc-action-approve {
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #c6f0df;
  background: #f0fdf7;
  color: #1d9e75;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    background: #1d9e75;
    color: #fff;
    border-color: #1d9e75;
  }
}

/* Document formatting in messages */
.cc-h1 {
  font-size: 16px;
  font-weight: 800;
  color: #2d2b55;
  margin: 12px 0 6px;
  padding-bottom: 4px;
  border-bottom: 2px solid #3ecfb4;
}
.cc-h2 {
  font-size: 14px;
  font-weight: 700;
  color: #2d2b55;
  margin: 10px 0 4px;
  padding-bottom: 3px;
  border-bottom: 1px solid #e4eaf6;
}
.cc-h3 {
  font-size: 13px;
  font-weight: 700;
  color: #444;
  margin: 8px 0 3px;
}
.cc-hr {
  border: none;
  border-top: 1px solid #e4eaf6;
  margin: 8px 0;
}

/* List items in messages */
.cc-list-item {
  display: block;
  padding-left: 4px;
  margin: 2px 0;
}
.cc-list-numbered {
  padding-left: 0;
}
.cc-list-num {
  font-weight: 700;
  color: #2d2b55;
  margin-right: 2px;
}

/* Markdown table — proper table layout */
/* Markdown tables: usan ::v-deep porque el HTML viene de v-html
   y no recibe el atributo data-v-* del scope. */
::v-deep .cc-md-table-wrap {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  margin: 10px 0 12px;
  border: 1px solid #d8dde8;
  border-radius: 8px;
  background: #fff;
  -webkit-overflow-scrolling: touch;
}
::v-deep .cc-md-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  table-layout: auto;
  margin: 0;

  th,
  td {
    padding: 9px 12px;
    border-bottom: 1px solid #e4eaf6;
    border-right: 1px solid #e4eaf6;
    text-align: left;
    vertical-align: middle;
    line-height: 1.4;
    white-space: nowrap;
    word-break: keep-all;
    overflow-wrap: normal;
    color: #303133;
  }
  th:last-child,
  td:last-child {
    border-right: none;
  }
  th {
    background: #f4f5f9;
    color: #2d2b55;
    font-weight: 700;
    font-size: 12px;
    border-bottom: 2px solid #d8dde8;
    white-space: normal;
    line-height: 1.25;
    text-transform: none;
  }
  tr:last-child td {
    border-bottom: none;
  }
  tr:nth-child(even) td {
    background: #fafbfd;
  }
  tr:hover td {
    background: #f4f5f9;
  }
  td:first-child,
  th:first-child {
    font-weight: 600;
    color: #2d2b55;
    white-space: normal;
    min-width: 110px;
  }
  td:not(:first-child) {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}

/* Legacy single-row table (kept for backward compat) */
.cc-table-row {
  display: flex;
  gap: 0;
  margin: 2px 0;
  font-size: 12px;
}
.cc-table-cell {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #e4eaf6;
  background: #fafbfd;
  &:first-child {
    font-weight: 600;
    color: #2d2b55;
    background: #f4f5f9;
  }
}

/* Tool executing indicator */
.cc-tool-executing {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #3ecfb4;
  font-weight: 500;
  margin-bottom: 6px;
  animation: cc-fade-in 0.2s ease;
}
.cc-tool-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #c6f0df;
  border-top-color: #3ecfb4;
  border-radius: 50%;
  animation: cc-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes cc-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes cc-fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Streaming cursor */
.cc-streaming::after {
  content: "▋";
  display: inline;
  color: #3ecfb4;
  animation: cc-blink 0.6s step-end infinite;
  margin-left: 1px;
}
@keyframes cc-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

/* Credit confirmation card */
.cc-credit-confirm {
  padding: 16px;
  background: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 12px;
  max-width: 420px;
}
.cc-confirm-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.cc-confirm-icon {
  font-size: 18px;
}
.cc-confirm-title {
  font-size: 14px;
  font-weight: 700;
  color: #92400e;
}
.cc-confirm-reason {
  font-size: 12.5px;
  color: #78716c;
  line-height: 1.4;
  margin-bottom: 8px;
}
.cc-confirm-balance {
  font-size: 12px;
  color: #57534e;
  margin-bottom: 12px;
  strong {
    color: #2d2b55;
  }
}
.cc-confirm-actions {
  display: flex;
  gap: 8px;
}
.cc-confirm-approve {
  padding: 7px 16px;
  border-radius: 8px;
  border: none;
  background: #2d2b55;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover:not(:disabled) {
    background: #1d9e75;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.cc-confirm-cancel {
  padding: 7px 16px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  background: #fff;
  color: #606266;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #f56c6c;
    color: #f56c6c;
  }
}

/* Limit exceeded banner */
.cc-limit-banner {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #fff8f0;
  border: 1px solid #fde2c8;
  border-radius: 12px;
  align-items: flex-start;
}
.cc-limit-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fef0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #e6a23c;
  font-size: 18px;
}
.cc-limit-body {
  flex: 1;
}
.cc-limit-title {
  font-size: 14px;
  font-weight: 700;
  color: #c7873a;
  margin-bottom: 4px;
}
.cc-limit-text {
  font-size: 13px;
  color: #8a6d3b;
  margin-bottom: 10px;
  line-height: 1.4;
}
.cc-limit-upgrade {
  padding: 6px 16px;
  border-radius: 8px;
  border: none;
  background: #2d2b55;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #3ecfb4;
  }
}

// ── Action proposal card ──────────────────────────────────────────────────────
.cc-action-proposal {
  background: #f8f9ff;
  border: 1.5px solid #c7d0f0;
  border-radius: 14px;
  padding: 16px;
  transition: border-color 0.2s, background 0.2s;

  &.cc-ap-done {
    background: #f0fdf8;
    border-color: #a7e9ce;
  }
  &.cc-ap-rejected {
    background: #fafafa;
    border-color: #e0e0e0;
    opacity: 0.7;
  }
  &.cc-ap-error {
    background: #fff8f0;
    border-color: #fde2c8;
  }
  &.cc-ap-historical {
    background: #fafbfc;
    border-color: #e4eaf6;
    opacity: 0.85;
  }
}
.cc-ap-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}
.cc-ap-icon {
  font-size: 24px;
  line-height: 1;
  flex-shrink: 0;
}
.cc-ap-titles {
  flex: 1;
}
.cc-ap-title {
  font-size: 14px;
  font-weight: 700;
  color: #2d2b55;
  line-height: 1.3;
}
.cc-ap-desc {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
  line-height: 1.4;
}
.cc-ap-badge {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  background: #eeedfe;
  color: #3c3489;
  white-space: nowrap;

  &.cc-ap-badge-done {
    background: #e1f5ee;
    color: #0f6e56;
  }
  &.cc-ap-badge-rejected {
    background: #f4f4f4;
    color: #888;
  }
  &.cc-ap-badge-error {
    background: #fff0e0;
    color: #b75000;
  }
  &.cc-ap-badge-historical {
    background: #eef1f6;
    color: #6b7280;
  }
}
.cc-ap-items {
  list-style: none;
  padding: 0;
  margin: 0 0 14px 0;
  border: 1px solid #e4eaf6;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;

  li {
    padding: 7px 12px;
    font-size: 12px;
    color: #444;
    border-bottom: 1px solid #f0f3fa;
    line-height: 1.4;

    &:last-child {
      border-bottom: none;
    }
  }
}
.cc-ap-hr {
  border: none;
  border-top: 1px solid #eee;
  margin: 2px 0;
}
.cc-ap-total-line {
  font-weight: 700;
  color: #2d2b55;
  font-size: 13px;
}
.cc-ap-result {
  font-size: 13px;
  color: #444;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  margin-top: 6px;
  line-height: 1.5;
}
.cc-ap-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.cc-ap-btn-approve {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border-radius: 10px;
  border: none;
  background: #1d9e75;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #158a62;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.cc-ap-btn-reject {
  padding: 8px 16px;
  border-radius: 10px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  color: #888;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #aaa;
    color: #555;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
.cc-ap-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #3ecfb4;
  font-weight: 500;
  margin-top: 4px;
}
.cc-ap-view-doc-wrap {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}
.cc-ap-btn-view-doc {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border-radius: 8px;
  background: #2d2b55;
  color: #fff;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
  &:hover {
    background: #3c3489;
  }
  &:active {
    transform: translateY(1px);
  }
  svg {
    flex-shrink: 0;
  }
}
.cc-ap-followup {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}
.cc-ap-followup-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  background: #f4f5f9;
  color: #2d2b55;
  border: 1px solid #d8dde8;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
  &:hover {
    background: #e9ecf5;
  }
  &:active {
    transform: translateY(1px);
  }
}
.cc-ap-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #c6f0df;
  border-top-color: #3ecfb4;
  border-radius: 50%;
  animation: cc-spin 0.7s linear infinite;
}

/* Typing dots */
.cc-typing-dots {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: #f4f5f9;
  border-radius: 16px;
  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #909399;
    animation: copilotBounce 1.4s infinite both;
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}
@keyframes copilotBounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Credits bar */
.cc-credits-bar {
  margin-bottom: 10px;
  padding: 8px 12px;
  background: #f8f9fc;
  border-radius: 10px;
  border: 1px solid #e4eaf6;
}
.cc-credits-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.cc-credits-icon {
  font-size: 13px;
}
.cc-credits-text {
  font-size: 12px;
  color: #303133;
  strong {
    font-weight: 700;
    color: #2d2b55;
  }
}
.cc-credits-detail {
  font-size: 11px;
  color: #909399;
}
.cc-credits-progress {
  height: 4px;
  background: #e4eaf6;
  border-radius: 2px;
  overflow: hidden;
}
.cc-credits-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}
.cc-credits-ok {
  background: #3ecfb4;
}
.cc-credits-warning {
  background: #e6a23c;
}
.cc-credits-critical {
  background: #f56c6c;
}

/* Input zone */
.cc-input-zone {
  padding: 16px 24px 20px;
  border-top: 1px solid #e4eaf6;
  background: #fff;
  flex-shrink: 0;
}
.cc-input-box {
  display: flex;
  align-items: flex-end;
  border: 1.5px solid #e4eaf6;
  border-radius: 12px;
  padding: 8px 12px;
  background: #fafbfd;
  transition: border-color 0.15s;
  &.focused {
    border-color: #3ecfb4;
    background: #fff;
  }
  textarea {
    flex: 1;
    border: none;
    outline: none;
    resize: none;
    font-size: 13.5px;
    line-height: 1.5;
    font-family: inherit;
    background: transparent;
    color: #303133;
    max-height: 120px;
    &::placeholder {
      color: #b0b3b8;
    }
    &:disabled {
      opacity: 0.6;
    }
  }
}
.cc-send-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  background: #2d2b55;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 8px;
  transition: background 0.15s;
  &:hover:not(:disabled) {
    background: #3ecfb4;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

/* Hints */
.cc-hints {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.cc-hint-label {
  font-size: 11px;
  color: #909399;
  font-weight: 500;
}
.cc-hint-chip {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid #e4eaf6;
  background: #fff;
  color: #606266;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #3ecfb4;
    color: #1d9e75;
    background: #f0fdf7;
  }
}

/* ── Document artifact layout ───────────────────── */
.copilot-chat {
  &.cc-has-doc-panel {
    flex-direction: row;
    .cc-main {
      flex: 0 0 42%;
      min-width: 320px;
      border-right: 1px solid #e4eaf6;
    }
  }
}
.cc-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
}

/* ── Document card (in chat bubble) ─────────────── */
.cc-doc-card {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 14px 16px;
  background: #fff;
  border: 1.5px solid #e4eaf6;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.18s;
  max-width: 340px;
  &:hover {
    border-color: #3ecfb4;
    box-shadow: 0 4px 16px rgba(62, 207, 180, 0.12);
    .cc-doc-card-arrow {
      color: #3ecfb4;
    }
  }
}
.cc-doc-card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f0fdf7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1d9e75;
  flex-shrink: 0;
}
.cc-doc-card-info {
  flex: 1;
  min-width: 0;
}
.cc-doc-card-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1d9e75;
  margin-bottom: 3px;
}
.cc-doc-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #2d2b55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cc-doc-card-hint {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}
.cc-doc-card-arrow {
  color: #c0c4cc;
  flex-shrink: 0;
  transition: color 0.18s;
}
/* Live (streaming) state for card */
.cc-doc-card-live {
  border-color: #3ecfb4;
  background: #f0fdf7;
  animation: cc-card-pulse 2s ease-in-out infinite;
  .cc-doc-card-icon {
    background: #d8f5ee;
  }
}
@keyframes cc-card-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(62, 207, 180, 0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(62, 207, 180, 0.15);
  }
}
/* Live badge with animated dot */
.cc-doc-badge-live {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #1d9e75;
}
.cc-doc-badge-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3ecfb4;
  animation: cc-dot-blink 1s ease-in-out infinite;
}
@keyframes cc-dot-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
}

/* ── Document preview panel ─────────────────────── */
.cc-doc-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #fafbfc;
  overflow: hidden;
}
.cc-doc-panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e4eaf6;
  background: #fff;
  flex-shrink: 0;
}
.cc-doc-panel-meta {
  flex: 1;
  min-width: 0;
}
.cc-doc-panel-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1d9e75;
  background: #e1f5ee;
  padding: 3px 8px;
  border-radius: 6px;
  margin-bottom: 6px;
}
/* Streaming panel: subtle top border indicator */
.cc-doc-panel-streaming {
  .cc-doc-panel-header {
    border-top: 2.5px solid #3ecfb4;
  }
}
.cc-doc-panel-title {
  font-size: 14px;
  font-weight: 700;
  color: #2d2b55;
  margin: 0;
  line-height: 1.3;
}
.cc-doc-panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.cc-doc-action-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1.5px solid #e4eaf6;
  background: #fff;
  color: #606266;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #3ecfb4;
    color: #1d9e75;
  }
  &.cc-doc-copied {
    border-color: #3ecfb4;
    color: #1d9e75;
    background: #f0fdf7;
  }
}
.cc-doc-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid #e4eaf6;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #606266;
  transition: all 0.15s;
  &:hover {
    border-color: #3ecfb4;
    color: #1d9e75;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
.cc-download-group {
  display: flex;
  gap: 4px;
}
.cc-doc-download-btn {
  width: auto;
  padding: 0 8px;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
}
.cc-dl-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.cc-doc-close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  transition: all 0.15s;
  &:hover {
    background: #f4f5f9;
    color: #2d2b55;
  }
}
.cc-doc-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
.cc-doc-content {
  font-size: 13.5px;
  line-height: 1.75;
  color: #303133;
  max-width: 680px;
  ::v-deep {
    strong {
      font-weight: 700;
      color: #2d2b55;
    }
    .cc-h1 {
      font-size: 18px;
      font-weight: 800;
      color: #2d2b55;
      margin: 0 0 16px;
      padding-bottom: 8px;
      border-bottom: 2px solid #e4eaf6;
    }
    .cc-h2 {
      font-size: 15px;
      font-weight: 700;
      color: #2d2b55;
      margin: 20px 0 8px;
    }
    .cc-h3 {
      font-size: 13px;
      font-weight: 700;
      color: #2d2b55;
      margin: 14px 0 6px;
    }
    .cc-hr {
      border: none;
      border-top: 1px solid #e4eaf6;
      margin: 16px 0;
    }
    .cc-list-item {
      display: block;
      padding: 2px 0 2px 4px;
    }
    .cc-list-numbered .cc-list-num {
      color: #3ecfb4;
      font-weight: 700;
      margin-right: 4px;
    }
    br {
      display: block;
      margin: 2px 0;
    }
  }
}

/* ── Slide transition ───────────────────────────── */
.cc-doc-slide-enter-active,
.cc-doc-slide-leave-active {
  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}
.cc-doc-slide-enter,
.cc-doc-slide-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
</style>
